import { useEffect, useRef, useState } from 'react'
import { useThree, useFrame } from '@react-three/fiber'
import { Text, Html, ContactShadows, PresentationControls, OrbitControls, Float, Environment, Sparkles } from '@react-three/drei'
import { Vector3 } from 'three'
import Model from './Computer'

export default function Experience({ loadingComplete, onMobileZoomIn, mobileIframeFullscreen })
{
    const { camera, size } = useThree()
    const [isHovering, setIsHovering] = useState(false)
    const [laptopOpened, setLaptopOpened] = useState(false)
    const [showIframe, setShowIframe] = useState(false)
    const defaultFov = useRef(camera.fov)
    const defaultPosition = useRef(camera.position.clone())
    const zoomPosition = useRef(camera.position.clone())
    const orbitControlsRef = useRef()
    
    // Mobile detection
    const isMobile = size.width < 768

    useEffect(() =>
    {
        // Set appropriate FOV for mobile vs desktop
        if (isMobile) {
            camera.fov = 100 // Wider FOV for mobile to see more of the scene
        } else {
            camera.fov = 45 // Original desktop FOV
        }
        camera.updateProjectionMatrix()
        
        defaultFov.current = camera.fov
        defaultPosition.current = camera.position.clone()

        // Desktop: original positioning
        zoomPosition.current = new Vector3(-1.5, 1.2, 2)
    }, [camera, isMobile])

    useFrame(() =>
    {
        // Only apply hover zoom effect on desktop, not mobile
        if (!isMobile) {
            const targetFov = isHovering ? Math.max(defaultFov.current * 0.92, 35) : defaultFov.current
            camera.fov += (targetFov - camera.fov) * 0.1
            camera.updateProjectionMatrix()

            const targetPosition = isHovering ? zoomPosition.current : defaultPosition.current
            const targetLookAt = isHovering ? new Vector3(0, 0.45, -1.15) : new Vector3(0, 0, 0)
            
            if (targetPosition)
            {
                camera.position.lerp(targetPosition, 0.08)
                // Look at the screen center when hovering, scene center when not
                const currentLookAt = new Vector3()
                camera.getWorldDirection(currentLookAt)
                const currentTarget = camera.position.clone().add(currentLookAt)
                const newTarget = currentTarget.lerp(targetLookAt, 0.08)
                camera.lookAt(newTarget)
            }
        }
    })

    // Trigger laptop opening animation only after loading screen completes
    useEffect(() => {
        if (!loadingComplete) return
        
        let timer, iframeTimer, mobileZoomTimer
        
        timer = setTimeout(() => {
            setLaptopOpened(true)
            // Show iframe after laptop finishes opening
            iframeTimer = setTimeout(() => {
                setShowIframe(true)
            }, 1500) // 1.5 seconds after laptop starts opening
        }, 500) // Start opening 0.5s after loading screen finishes
        
        return () => {
            clearTimeout(timer)
            clearTimeout(iframeTimer)
        }
    }, [loadingComplete, isMobile, camera])

    return <>
        <color attach="background" args={['#0f172a']} />

        {/* Add environment lighting */}
        <Environment 
            preset="studio"
            background={false}
            backgroundIntensity={0.3}
        />
        <ambientLight intensity={0.5} />
        <directionalLight 
            position={[5, 5, 5]} 
            intensity={1.5}
            castShadow
            shadow-mapSize={[1024, 1024]}
        />

        {/* Atmospheric sparkles */}
        <Sparkles 
            count={100}
            scale={10}
            size={2}
            speed={0.4}
            opacity={0.6}
            color="#64ffda"
        />

        {/* Floating geometric shapes */}
        {Array.from({ length: 5 }).map((_, i) => (
            <Float key={i} speed={1.5 + i * 0.5} rotationIntensity={0.5}>
                <mesh position={[
                    (Math.random() - 0.5) * 10,
                    (Math.random() - 0.5) * 5,
                    (Math.random() - 0.5) * 10
                ]}>
                    <boxGeometry args={[0.1, 0.1, 0.1]} />
                    <meshStandardMaterial 
                        color="#64ffda" 
                        transparent 
                        opacity={0.3}
                        wireframe
                    />
                </mesh>
            </Float>
        ))}

        {/* Conditional Controls: OrbitControls for mobile, PresentationControls for desktop */}
        {isMobile ? (
            <>
                <OrbitControls
                    ref={orbitControlsRef}
                    maxDistance={8}
                    dampingFactor={0.1}
                    enableDamping={true}
                    target={[0, 0, 0]}
                    makeDefault
                />
                <Float rotationInensity={0.2}>
                    {/* ScreenLight - back to original position */}
                    <rectAreaLight 
                        width={2.5}
                        height={1.65}
                        intensity={65}
                        color={'#62cbf5ff'}
                        rotateOnAxis={[0.1, Math.PI, 0]}
                        position={ [ 0, 0.55, -1.25 ] }
                    />
                        {/* Position laptop with mobile-responsive positioning */}
                        <Model 
                            position={[0, -0.8, 0]} 
                            rotation-x={0.13}
                            scale={0.8}
                            laptopOpened={laptopOpened}
                            castShadow
                        />
                        
                        {/* Screen with mobile-responsive positioning (visual only) */}
                        {showIframe && !mobileIframeFullscreen && (
                            <Html
                            transform
                            wrapperClass='htmlScreen'
                            distanceFactor={1.17}
                            position={[.021, 0.72, -1.40]}
                            rotation-x={-0.22}
                            >
                                <div className="iframe-container">
                                    <iframe 
                                        src='https://3d-portfolio-three-opal.vercel.app/' 
                                        style={{ pointerEvents: 'none' }}
                                    />
                                </div>
                            </Html>
                        )}

                        {/* Tap button opens fullscreen overlay outside Canvas */}
                        {showIframe && !mobileIframeFullscreen && (
                            <Html
                            transform
                            wrapperClass='zoomButton'
                            distanceFactor={1.08}
                            position={[.21, -.81, -1.40]}
                            rotation-x={-0.22}
                            scale={5}
                            >
                                <button 
                                    className="mobile-zoom-in-btn"
                                    onClick={onMobileZoomIn}
                                >
                                    🔍 Tap to View Portfolio
                                </button>
                            </Html>
                        )}

                        <Text
                            font='./bangers-v20-latin-regular.woff'
                            fontSize={0.35}
                            position={[1.5, 0.5, 0.5]}
                            rotation-y={-1.25}
                            maxWidth={1.5}
                            textAlign='center'
                        >
                            Devontay Cross
                            </Text>
                </Float>
            </>
        ) : (
            <PresentationControls 
                global
                rotation={[0.13, 0.1, 0]}
                polar={[-0.4, 0.2]}
                azimuth={[-1, 0.75]}
                damping={0.1}
                snap
                >
                <Float rotationInensity={0.2} enabled={!isHovering}>
                    {/* ScreenLight - back to original position */}
                    <rectAreaLight 
                        width={2.5}
                        height={1.65}
                        intensity={65}
                        color={'#62cbf5ff'}
                        rotateOnAxis={[0.1, Math.PI, 0]}
                        position={ [ 0, 0.55, -1.25 ] }
                    />
                        {/* Position laptop with desktop positioning */}
                        <Model 
                            position={[0, -1.2, 0]} 
                            rotation-x={0.13}
                            scale={1}
                            laptopOpened={laptopOpened}
                            castShadow
                        />
                        
                        {/* Screen with desktop positioning */}
                        {showIframe && (
                            <Html
                            transform
                            wrapperClass='htmlScreen'
                            distanceFactor={1.17}
                            position={[0, 0.45, -1.1]}
                            rotation-x={-0.13}
                            >
                                <div
                                    className="iframe-container"
                                    onMouseEnter={() => setIsHovering(true)}
                                    onMouseLeave={() => setIsHovering(false)}
                                >
                                    {/* Resume URL */}
                                    <iframe src='https://3d-portfolio-three-opal.vercel.app/' />
                                </div>
                            </Html>
                        )}

                        <Text
                            font='./bangers-v20-latin-regular.woff'
                            fontSize={1}
                            position={[2, 0.75, 0.75]}
                            rotation-y={-1.25}
                            maxWidth={2}
                            textAlign='center'
                        >
                            Devontay Cross
                            </Text>
                </Float>
            </PresentationControls>
        )}
        <ContactShadows 
            position-y={- 1.6} 
            opacity={ 0.6 }
            scale={ 5 }
            blur={ 2.4 }
            />
    </>
}